// COMO ESCREVER UMA BOA CENA — `T11.2` e `T11.4`, e os dois pedem a mesma peça.
//
// ── OS DOIS PONTOS, E POR QUE ELES SÃO UM ───────────────────────────────────────────────────
//
//   T11.2  *"A explicação fala do processo, mas não de como escrever bem a história. As dicas que
//          evitam os erros comuns estão só no Instagram."*
//          Recomendação: *"acrescentar, ao lado do formulário de criação, um bloco curto «Como
//          escrever uma boa cena»"*.
//
//   T11.4  *"É a única função que tem que ser calibrada, pois precisa ser muito detalhada, e
//          quanto mais texto, mais confuso fica o resultado."*
//          Recomendação: *"levar as três dicas para dentro do formulário, como uma checagem antes
//          de gerar: limite de tamanho por cena, alerta de texto longo, pergunta «qual é o momento
//          principal?» e sugestão de variar o ritmo."*
//
// ── O ACHADO QUE MUDA O TEXTO DAS DICAS, E ELE É MEDIDO NO PRÓPRIO PRODUTO ──────────────────
//
// *"Quanto mais texto, mais confuso fica o resultado"* não é impressão: é **truncamento em
// silêncio**, e os três números estão no código, em três arquivos diferentes.
//
//     o campo de texto aceita ............................. 20.000 caracteres
//     a máquina monta no máximo ........................... 40 quadros   (`MAX_BATIDAS`)
//     o PEDIDO DE DESENHO de cada cena usa ................ 90 caracteres (`MAX_CENA`)
//
// Noventa. Uma cena com 200 caracteres é desenhada a partir dos primeiros 90 — o resto é cortado
// e **nada na tela diz isso**. É por isso que texto comprido sai confuso: a criança descreve a
// cena inteira, e o desenhador recebe a primeira frase e meia.
//
// Então a dica não é *"evite texto longo"*, que é adjetivo. É o número, e ele vem daqui:
// `MAX_CENA` do `prompt-master` e `MAX_BATIDAS` do `quebrador-llm`, no backend. **Se um deles
// mudar lá e não mudar aqui, a tela passa a mentir** — e é isso que o teste desta peça guarda,
// com os números escritos por extenso nele.
//
// O exemplo que a própria tela oferece tem linhas de 42, 46, 48 e 61 caracteres: todas abaixo do
// corte. É por isso que o exemplo funciona bem, e é a prova de que o limite não é apertado — ele
// só é invisível.
//
// ── POR QUE ISTO É UMA FUNÇÃO PURA, E NÃO UM BLOCO DE JSX ───────────────────────────────────
//
// Porque são duas coisas: um bloco que a tela sempre mostra (as três dicas, que o `T11.2` pede) e
// uma checagem do que a criança escreveu AGORA (o alerta, que o `T11.4` pede). As duas falam das
// mesmas regras, e escrever as regras em dois lugares é garantir que um dos dois envelheça.
//
// ── E A CHECAGEM AVISA, NUNCA RECUSA ───────────────────────────────────────────────────────
//
// Esta é a mesma regra que o `T4.2` já fixou nesta tela, sobre o aviso de "letras soltas": ela
// PERGUNTA, nunca recusa. Uma história comprida continua podendo ser gerada — o que muda é a
// criança saber, antes de esperar três minutos, o que vai ser cortado.

/**
 * Quantas letras de cada cena chegam ao pedido de desenho.
 *
 * O número é `MAX_CENA` do `prompt-master` do backend. Ele existe porque o codificador de texto do
 * desenhador tem 77 fichas de orçamento, e o que passa disso é cortado pelo fim — onde mora o
 * texto de segurança. Cortar a cena ANTES é o que garante que a trava de conteúdo nunca caia fora
 * da janela.
 */
export const LETRAS_QUE_O_DESENHO_USA = 90;

/**
 * E QUANTAS LETRAS SOBRAM QUANDO A PESSOA DESCREVE O PERSONAGEM — T16.2 e T16.3.
 *
 * O orçamento do pedido de desenho é de 300 letras e a cauda que a plataforma cola — a estética
 * mais a frase de segurança — ocupa 203 delas. Sobram 95 para tudo o que a criança escreveu, e
 * quando ela também descreve o personagem essa descrição tem 50 letras guardadas. A cena fica com
 * 45.
 *
 * Até 03/10 a tela dizia 90 nos dois casos, e com o campo preenchido a descrição era descartada em
 * silêncio — o QA mediu **zero** das palavras dela chegando ao desenho. Dizer 90 quando são 45 é a
 * mesma mentira por outro lado: a dica existe para a criança saber onde cortar.
 */
export const LETRAS_QUE_O_DESENHO_USA_COM_PERSONAGEM = 45;

/** Quantas letras da descrição do personagem chegam ao pedido. É a reserva do backend. */
export const LETRAS_DO_PERSONAGEM_QUE_O_DESENHO_USA = 50;

/** Quantos quadros a separação automática monta, no máximo. `MAX_BATIDAS` do `quebrador-llm`. */
export const QUADROS_QUE_A_MAQUINA_MONTA = 40;

/** As três dicas, e elas são as do Instagram do projeto, nomeadas no próprio ponto. */
export interface Dica {
  id: "tamanho" | "descricao" | "foco" | "ritmo";
  titulo: string;
  texto: string;
}

/**
 * AS DICAS COM OS NÚMEROS QUE ESTÃO VALENDO — T17.3.
 *
 * `DICAS` continua existindo, com os números padrão, porque é o que a tela mostra enquanto a
 * resposta do serviço não chegou — e porque meia dúzia de testes já afirmam sobre ela.
 *
 * Quem tem os números do serviço chama `dicasCom`. Dizer 90 quando o painel pôs 60 é a mesma
 * mentira que o T16.3 custou uma rodada de QA para achar.
 */
/**
 * T26.R7 · A DICA DE TAMANHO DIZ O QUE O DESENHO FAZ HOJE. Ela dizia «o desenho usa as primeiras 90
 * letras — 45 se você descrever o personagem», que era o orçamento do desenhista de reserva. O
 * principal lê a linha inteira e a descrição inteira; o limite que sobra é o do QUADRO: uma ação.
 */
export const TEXTO_DA_DICA_DE_TAMANHO =
  "Cada linha vira um quadro. O desenho lê a linha inteira e a descrição do personagem, mas um " +
  "quadro mostra bem UMA ação: “A Bia procura o caderno debaixo da mesa” é uma cena; um parágrafo é quatro.";

export function dicasCom(n: { maxCena: number; maxCenaComPersonagem: number }): Dica[] {
  return DICAS.map((d) =>
    d.id !== "tamanho"
      ? d
      : {
          ...d,
          texto:
            TEXTO_DA_DICA_DE_TAMANHO,
        }
  );
}

export const DICAS: Dica[] = [
  {
    id: "tamanho",
    titulo: "Uma cena por linha, e curta",
    texto:
      TEXTO_DA_DICA_DE_TAMANHO,
  },
  {
    // T12.7 · O EXEMPLO DE CENA BEM DESCRITA, que o relatório de teste com usuário pede por
    // escrito: *"um campo opcional «Quem é o personagem» e um exemplo de cena bem descrita"*. O
    // par abaixo é a mesma cena das duas formas, porque a diferença entre elas é a informação.
    id: "descricao",
    titulo: "Diga o que se vê, não o que se sente",
    texto:
      "O desenhador não sabe o que é raiva: ele sabe desenhar sobrancelha franzida e punho " +
      "fechado. Em vez de “ele surta”, escreva “ele fecha os punhos e grita, de sobrancelha " +
      "franzida”. Vale para o lugar também: “no pátio da escola, com o muro pintado atrás”.",
  },
  {
    id: "foco",
    titulo: "Qual é o momento principal?",
    texto:
      "Escolha a cena mais importante da sua história e dê a ela o quadro mais claro. " +
      "Histórias em que tudo tem a mesma importância saem sem nada para olhar.",
  },
  {
    id: "ritmo",
    titulo: "Varie o ritmo",
    texto:
      "Não conte tudo na mesma velocidade. Uma cena larga para mostrar o lugar, duas rápidas " +
      "para a ação, uma só com o rosto de quem descobriu alguma coisa.",
  },
];

/** Um problema MEDIDO no que a criança escreveu agora — não uma dica. */
export interface Alerta {
  id: "cena-longa" | "cenas-demais";
  /** A frase, já com os números dentro. */
  texto: string;
  /** Em que linhas (1 em diante) o problema está. Vazio quando o alerta é da história inteira. */
  linhas: number[];
}

export interface LeituraDoTexto {
  /** As linhas não vazias, que é o que a separação automática trata como cena. */
  cenas: string[];
  alertas: Alerta[];
}

const plural = (n: number, um: string, muitos: string) => (n === 1 ? um : muitos);

/**
 * O que há de medível no texto escrito até agora.
 *
 * Texto vazio devolve zero cenas e zero alertas — a tela mostra as dicas e cala sobre o resto, que
 * é o certo para quem ainda não escreveu nada.
 */
/** T26.R7 · quantas letras uma linha pode ter antes de virar «muita coisa para um quadro só». */
export const LETRAS_DE_UM_QUADRO = 400;

export function lerOTexto(
  texto: string,
  descreveuOPersonagem = false,
  numeros?: { maxCena: number; maxCenaComPersonagem: number }
): LeituraDoTexto {
  // O teto muda com o campo do personagem, porque o orçamento do pedido de desenho é um só e os
  // dois textos o dividem. Quem chama sem o segundo argumento continua recebendo o de antes.
  //
  // E o TERCEIRO argumento são os números que o serviço está usando agora (T17): sem ele a tela
  // alertaria pelo número de ontem no dia em que alguém mudasse o valor no painel.
  const padrao = {
    maxCena: LETRAS_QUE_O_DESENHO_USA,
    maxCenaComPersonagem: LETRAS_QUE_O_DESENHO_USA_COM_PERSONAGEM,
  };
  const n = numeros ?? padrao;
  // T26.R7 · O TETO É O DO DESENHISTA PRINCIPAL. O relatório de 09/10 viu «o desenho vai ser pedido
  // com as primeiras 45. São 45 e não 90 porque a descrição do personagem também entra no pedido»
  // — e isso era o orçamento do desenhista de RESERVA. O principal (FLUX.2 klein) recebe a cena e a
  // descrição inteiras, traduzidas (T24). Aqui só se avisa linha longa demais para UM quadro.
  void descreveuOPersonagem;
  void n;
  const tetoDaCena = LETRAS_DE_UM_QUADRO;
  const cenas = texto
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const alertas: Alerta[] = [];

  const longas = cenas
    .map((c, i) => ({ n: i + 1, letras: c.length }))
    .filter((c) => c.letras > tetoDaCena);

  if (longas.length) {
    // O NÚMERO DA PIOR LINHA entra na frase, e não a média: a criança procura a linha para
    // encurtar, e a média não aponta para nenhuma.
    const pior = longas.reduce((a, b) => (a.letras >= b.letras ? a : b));
    alertas.push({
      id: "cena-longa",
      texto:
        `${longas.length} ${plural(longas.length, "cena está", "cenas estão")} com muita coisa ` +
        `para um quadro só. A ${plural(longas.length, "linha", "maior é a linha")} ${pior.n} tem ` +
        `${pior.letras} letras. O desenho lê a cena inteira, mas um quadro mostra bem uma ação: ` +
        `quebre ${plural(longas.length, "ela", "as mais longas")} em duas linhas.`,
      linhas: longas.map((c) => c.n),
    });
  }

  if (cenas.length > QUADROS_QUE_A_MAQUINA_MONTA) {
    alertas.push({
      id: "cenas-demais",
      texto:
        `Você escreveu ${cenas.length} cenas, e a montagem automática faz no máximo ` +
        `${QUADROS_QUE_A_MAQUINA_MONTA} quadros. As de baixo não vão virar quadro. ` +
        `Vale guardar o resto para um segundo quadrinho.`,
      linhas: [],
    });
  }

  return { cenas, alertas };
}
